import Button from "app/storybookComponents/Button";
import { forwardRef, useState } from "react";
import { Card } from "react-bootstrap";
import { BlindSpot } from "./types";
import SectionHeader from "./SectionHeader";
import useCollapsibleSection from "./useCollapsibleSection";

interface Props {
  readonly blindSpots: BlindSpot[];
  readonly forceExpanded?: boolean;
  // When blindSpots is a partial page/chunk (PDF export), this is the true
  // count across all chunks, so the summary line stays accurate.
  readonly totalBlindSpotCount?: number;
}

const TeamBlindSpotReport = forwardRef<HTMLDivElement, Props>(
  function TeamBlindSpotReport(
    { blindSpots, forceExpanded, totalBlindSpotCount },
    ref
  ) {
    const [blindSpotIndex, setBlindSpotIndex] = useState(0);
    const { collapsed, toggleIsCollapsed } = useCollapsibleSection(
      forceExpanded,
      true
    );

    const setNewBlindSpotIndex = () => {
      if (!blindSpots.length) {
        return 0;
      }
      const newIndex = (blindSpotIndex + 1) % blindSpots.length;

      setBlindSpotIndex(newIndex);
    };

    if (!blindSpots.length) {
      return null;
    }

    if (collapsed) {
      return (
        <Card ref={ref}>
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="Team Blindspot Report"
            isCollapsed={collapsed}
          />
        </Card>
      );
    }

    const getBlindSpotContent = (blindSpot: BlindSpot, key: number) => {
      let reflectionQuestionsCount = 0;
      let actionStepsCount = 0;
      return (
        <div className="column-gap-20px" key={key}>
          <div className="card-with-left-strip column-gap-8px dark-blue card workplace-insights-simple-card-with-side-strip">
            <div>
              <h3>{blindSpot.trait}</h3>
              <p>{blindSpot.description}</p>
            </div>
          </div>
          <div className="responsive-two-column-container">
            <Card
              style={{
                minHeight: "148px",
              }}
              className="workplace-insights-simple-card"
            >
              <h3>Strengths</h3>
              <p>{blindSpot.strength}</p>
            </Card>
            <Card
              style={{
                minHeight: "148px",
              }}
              className="workplace-insights-simple-card"
            >
              <h3>Risks</h3>
              <p>{blindSpot.risk}</p>
            </Card>
          </div>
          <div
            className="responsive-two-column-container"
            style={{ marginBottom: "20px" }}
          >
            <Card
              style={{
                minHeight: "148px",
                fontSize: "14px",
              }}
              className="workplace-insights-simple-card"
            >
              <h3>Reflection Questions</h3>
              <ul>
                {blindSpot.reflectionQuestions.map((question) => {
                  reflectionQuestionsCount += 1;
                  return <li key={reflectionQuestionsCount}>{question}</li>;
                })}
              </ul>
            </Card>
            <Card
              style={{
                minHeight: "148px",
                fontSize: "14px",
              }}
              className="workplace-insights-simple-card"
            >
              <h3>Action Items</h3>
              <ul>
                {blindSpot.actionSteps.map((actionStep) => {
                  actionStepsCount += 1;
                  return <li key={actionStepsCount}>{actionStep}</li>;
                })}
              </ul>
            </Card>
          </div>
        </div>
      );
    };

    return (
      <Card
        ref={ref}
        style={{
          minHeight: "550px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div className="column-gap-20px">
          <SectionHeader
            toggleIsCollapsed={toggleIsCollapsed}
            title="Team Blindspot Report"
            isCollapsed={collapsed}
            hideActionButton={forceExpanded}
          />
          <p>
            Your team has {totalBlindSpotCount ?? blindSpots.length} blindspots.
          </p>
          {forceExpanded
            ? blindSpots.map((blindSpot, index) =>
                getBlindSpotContent(blindSpot, index)
              )
            : getBlindSpotContent(blindSpots[blindSpotIndex], blindSpotIndex)}
        </div>
        {forceExpanded ? null : (
          <Button
            onClick={setNewBlindSpotIndex}
            style={{ alignSelf: "flex-start" }}
            variant="secondary-blue"
          >
            See another blindspot
          </Button>
        )}
      </Card>
    );
  }
);

export default TeamBlindSpotReport;
